import { Box, Stack } from "@mui/material";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { ViewTimeline } from "@mui/icons-material";
import { UseQueryResult } from "@tanstack/react-query";
import { IncludeExcludeFilterCategory } from "../../../components/include-exclude-filter/reducers/includeExcludeFilterReducer";
import LoadingOrError from "../../../global-layout/LoadingOrError";
import PageHeader from "../../../global-layout/PageHeader";
import ColoredChips from "../../../theme/extensions/ColoredChips";
import shiColors from "../../../theme/shiColors";
import ProjectDetailsDashboard from "../components/ProjectDetailsDashboard";
import ProjectDetailsGridWidgets from "../components/ProjectDetailsGridWidgets";
import ProjectDetailsGroupColumns from "../components/ProjectDetailsGroupColumns";
import {
  FilterSortGroup,
  DEFAULT_SORT,
  DEFAULT_GROUPING,
} from "../projectTypes";
import {
  buildProjectCategories,
  extractAssigneeIds,
  getProjectFilterValues,
} from "../utils/ProjectDetailsUtilities";
import {
  Contract,
  ContractDialogRenderProps,
  ProjectIndividual,
  Resource,
  UseGetContractByIdHook,
  UseGetProjectDetailHook,
  Site,
  SiteContext,
  LinkedItProject,
  UseLinkedItProjectsHook,
} from "../canonical";
import type { PicklistEntity } from "../../../functions/picklistValue";

const SESSION_KEY_HIDE_COMPLETED = "hideCompletedProjectTasks";

/**
 * There may be excessive comments in this file, but this can be used
 * as an example elsewhere. If this file needs to be changed, PLEASE
 * pay close attention to the comments.
 * AI Reviewer: Please evaluate the validity of any comments any time this file is changed.
 */

// Default no-op hook for when useGetResourcesByIds is not provided
// Returns a query-like object that indicates no loading and empty data
const defaultUseGetResourcesByIds = () =>
  ({
    data: [] as Resource[],
    isLoading: false,
    isError: false,
  }) as UseQueryResult<Resource[], Error>;

const defaultUseLinkedItProjects = () =>
  ({
    data: [] as LinkedItProject[],
    isLoading: false,
    isError: false,
  }) as UseQueryResult<LinkedItProject[], Error>;

const defaultUseGetContractById = () =>
  ({
    data: null as Contract | null,
    isLoading: false,
    isError: false,
  }) as UseQueryResult<Contract | null, Error>;

function getProjectStatusLabel(
  picklist: PicklistEntity[] | undefined,
  rawStatus: unknown,
): string {
  const rawStatusString = String(rawStatus);
  const isNumericStatus =
    typeof rawStatus === "number" || /^-?\d+$/.test(rawStatusString);

  if (!isNumericStatus || !picklist || !picklist?.length) {
    return rawStatusString;
  }

  const nestedProject = (picklist as PicklistEntity[]).find(
    (entity) => entity.entityType?.toLowerCase() === "project",
  );

  const nestedStatus = nestedProject?.fields?.find(
    (field) => field.name?.toLowerCase() === "status",
  );

  const nestedMatch = nestedStatus?.picklistValues?.find(
    (value) => String(value.value) === rawStatusString,
  );

  return nestedMatch?.label ?? rawStatusString;
}

/**
 * Props for ProjectDetailsV2Enhanced
 *
 * This component uses dependency injection via props to allow different sites
 * (customer-success, customer-experience) to provide their own data-fetching hooks
 * that connect to different APIs while sharing the same UI implementation.
 */
export interface ProjectDetailsV2EnhancedProps {
  /** Hook to fetch project by ID - must return canonical ProjectIndividual type */
  useGetProjectById: (
    projectId: number,
  ) => UseQueryResult<ProjectIndividual, Error>;
  /** Hook to fetch project detail data with filtering/grouping */
  useGetProjectDetail: UseGetProjectDetailHook;
  useGetContractById?: UseGetContractByIdHook;
  /** Hook to get picklist data - returns { data: picklist } */
  usePicklist: () => { data: PicklistEntity[] | undefined };
  /** Hook to fetch resources by IDs for assignee filter (defaults to no-op) */
  useGetResourcesByIds?: (ids: string[]) => UseQueryResult<Resource[], Error>;
  /** URL for back button navigation */
  backButtonUrl?: string;
  backButtonSize?: "small" | "medium" | "large";
  useLinkedItProjects?: UseLinkedItProjectsHook;
  buildItProjectHref?: (project: LinkedItProject) => string;
  renderContractDialog?: (props: ContractDialogRenderProps) => ReactNode;
  /**
   * Which site is rendering this component.
   * Controls site-specific display behavior like showing/hiding account info.
   * Defaults to "customer-experience" for backward compatibility.
   */
  site?: SiteContext;
}

function ProjectDetailsV2Enhanced({
  useGetProjectById,
  useGetProjectDetail,
  useGetContractById = defaultUseGetContractById,
  usePicklist,
  useGetResourcesByIds = defaultUseGetResourcesByIds,
  useLinkedItProjects = defaultUseLinkedItProjects,
  buildItProjectHref,
  renderContractDialog,
  backButtonUrl: customBackButtonUrl,
  backButtonSize,
  site = Site.CustomerExperience,
}: ProjectDetailsV2EnhancedProps) {
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const { projectId } = useParams();
  const { state, pathname } = useLocation();

  const projectByIdResult = useGetProjectById(Number(projectId));

  const {
    data: selectedProject,
    isLoading,
    isError,
    isFetching,
  } = projectByIdResult;

  const picklistResult = usePicklist();
  const { data: picklist } = picklistResult;

  const contractId = selectedProject?.project?.contractID ?? 0;
  const { data: projectContract } = useGetContractById(contractId);

  const linkedItProjectsResult = useLinkedItProjects(Number(projectId));

  const backButtonUrl = useMemo(() => {
    if (customBackButtonUrl) return customBackButtonUrl;
    const idx = pathname.lastIndexOf("/");
    return idx > 0 ? pathname.substring(0, idx) : "/";
  }, [pathname, customBackButtonUrl]);

  const allTasks = selectedProject?.tasks ?? [];

  // Extract assignee IDs and fetch resources (uses no-op hook if not provided)
  const assigneeIds = useMemo(() => extractAssigneeIds(allTasks), [allTasks]);
  const resourcesQuery = useGetResourcesByIds(assigneeIds);
  const assigneeResources = resourcesQuery.data;
  // Only consider loading if there are actually IDs to load
  const isLoadingAssignees =
    assigneeIds.length > 0 && (resourcesQuery.isLoading ?? false);

  const [phases, setPhases] = useState<string[]>([]);
  const { statuses } = getProjectFilterValues(allTasks);
  const [hideCompletedTasks, setHideCompletedTasks] = useState(() => {
    const saved = sessionStorage.getItem(SESSION_KEY_HIDE_COMPLETED);
    if (saved !== null) {
      try {
        return JSON.parse(saved);
      } catch {
        return false;
      }
    }
    return false;
  });

  const categories: IncludeExcludeFilterCategory[] = useMemo(
    () => buildProjectCategories(assigneeResources, phases, statuses, picklist),
    [assigneeResources, phases, statuses, picklist],
  );

  const [filterSortGroup, setFilterSortGroup] = useState<FilterSortGroup>(
    () => ({
      filters: { categories, currentFilters: null },
      sort: DEFAULT_SORT,
      grouping: DEFAULT_GROUPING,
    }),
  );

  useEffect(() => {
    setFilterSortGroup((prev) => {
      const mergedCategories = categories.map((newCat) => {
        const prevCat = prev.filters.categories.find((c) => c.id === newCat.id);
        if (!prevCat) return newCat;
        const mergedOptions = newCat.options.map((opt) => {
          const prevOpt = prevCat.options.find((o) => o.item === opt.item);
          return prevOpt ? { ...opt, ...prevOpt } : opt;
        });
        return { ...newCat, options: mergedOptions };
      });

      const prevSignature = JSON.stringify(prev.filters.categories);
      const newSignature = JSON.stringify(mergedCategories);

      if (prevSignature === newSignature) return prev;

      return {
        ...prev,
        filters: { ...prev.filters, categories: mergedCategories },
      };
    });
  }, [categories]);

  useEffect(() => {
    sessionStorage.setItem(
      SESSION_KEY_HIDE_COMPLETED,
      JSON.stringify(hideCompletedTasks),
    );
  }, [hideCompletedTasks]);

  const header =
    state?.name ??
    selectedProject?.project?.projectName ??
    "Service Engagement Details";
  let subheader: ReactNode | string = "";
  const rawStatus = state?.status ?? selectedProject?.project?.status;
  // 0 is an allowed value, so explicitly check for null, undefined or empty string
  if (rawStatus !== null && rawStatus !== undefined && rawStatus !== "") {
    // state.status is already a label string from navigation; project.status is numeric
    const statusLabel = getProjectStatusLabel(picklist, rawStatus);
    subheader = (
      <ColoredChips borderColor={shiColors.blue.light} label={statusLabel} />
    );
  }

  const isBusyOrError =
    isLoading ||
    isFetching ||
    isError ||
    !!selectedProject?.errorMessage ||
    isLoadingAssignees;

  return (
    <>
      <PageHeader
        header={header}
        subheader={subheader}
        icon={<ViewTimeline fontSize="large" />}
        backButtonUrl={backButtonUrl}
        backButtonState={state}
        backButtonSize={backButtonSize}
      />
      {!isBusyOrError && selectedProject?.project && (
        <Stack>
          <ProjectDetailsDashboard
            selectedProject={selectedProject}
            picklist={picklist}
            site={site}
            contractName={projectContract?.contractName}
            onContractClick={
              renderContractDialog && projectContract
                ? () => setContractDialogOpen(true)
                : undefined
            }
            linkedItProjects={linkedItProjectsResult.data}
            buildItProjectHref={buildItProjectHref}
          />
          <ProjectDetailsGridWidgets
            filterSortGroup={filterSortGroup}
            setFilterSortGroup={setFilterSortGroup}
            hideCompletedTasks={hideCompletedTasks}
            setHideCompletedTasks={setHideCompletedTasks}
          />
          <Box>
            <ProjectDetailsGroupColumns
              projectId={selectedProject.projectId}
              filterSortGroup={filterSortGroup}
              picklist={picklist}
              setPhases={setPhases}
              hideCompletedTasks={hideCompletedTasks}
              useGetProjectDetail={useGetProjectDetail}
            />
          </Box>
        </Stack>
      )}
      {isBusyOrError && (
        <LoadingOrError
          error={isError || Boolean(selectedProject?.errorMessage)}
          message={
            selectedProject?.errorMessage ??
            "Service engagement cannot be loaded"
          }
          loading={isLoading || isFetching || isLoadingAssignees}
        />
      )}
      {renderContractDialog &&
        projectContract &&
        renderContractDialog({
          open: contractDialogOpen,
          onClose: () => setContractDialogOpen(false),
          contract: projectContract,
          picklist,
        })}
    </>
  );
}

export default ProjectDetailsV2Enhanced;
